/**
 * ChromaKeyVideo — Live-Chroma-Key im Browser via WebGL.
 *
 * Warum? libvpx-vp9 im Sandbox-ffmpeg (Debian) ist ohne VP9-Alpha kompiliert,
 * HEVC-Alpha in MOV braucht eine spezielle Toolchain. Daher realisieren wir
 * Chroma-Keying nicht als encodierter Alpha-Kanal im Video, sondern
 * berechnen den Alpha-Kanal frame-genau im Browser mit einem winzigen
 * Fragment-Shader. Vorteile:
 *   - Ein einziges MP4 als Source, funktioniert in Chrome/Firefox/Safari/iOS.
 *   - Kein Codec-Kompatibilitätsproblem.
 *   - Chroma-Farbe/Toleranz sind CMS-konfigurierbar (`media.chroma_key`).
 *
 * Rendering:
 *   1. Verstecktes <video> streamt das Original-MP4 (autoplay, muted, loop).
 *   2. Auf ein <canvas> wird jeder Frame per WebGL-Draw gekeyt.
 *   3. Fragment-Shader macht color-distance-Test + weichen Übergang
 *      (smoothstep) + Grün-Despill an den Kanten.
 *
 * Fallback: Bei WebGL-Fehler oder onError → statisches PNG-Logo.
 */
import { useEffect, useRef, useState } from "react";

export type ChromaKeyConfig = {
    /** Hex-Farbe des Chroma-Backgrounds, z. B. "#80A028". */
    color: string;
    /** Toleranz (0..1). Größer = mehr weggeschnitten (Standard 0.35). */
    similarity?: number;
    /** Weichheit des Übergangs (0..0.5). Größer = weichere Kante (Standard 0.15). */
    blend?: number;
    /** Grün-Despill-Stärke (0..1). Standard 1.0 = voll. */
    despill?: number;
};

interface Props {
    src: string;
    /** Optional VP9 WebM sibling for browsers without H.264 (also better for
     *  fast-startup in Chromium sandbox / Playwright headless). */
    srcWebm?: string | null;
    poster?: string;
    chroma: ChromaKeyConfig;
    className?: string;
    testId?: string;
    ariaLabel?: string;
    onClick?: () => void;
    onError?: () => void;
    /** Wird bei jeder Route-Änderung aufgerufen, damit currentTime reset. */
    restartKey?: string;
}

const VERT = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
    v_uv = vec2((a_pos.x + 1.0) * 0.5, 1.0 - (a_pos.y + 1.0) * 0.5);
    gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const FRAG = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_frame;
uniform vec3 u_key;
uniform float u_similarity;
uniform float u_blend;
uniform float u_despill;
void main() {
    vec4 c = texture2D(u_frame, v_uv);
    // Distanz im RGB-Raum zum Key
    float d = distance(c.rgb, u_key);
    // Alpha wächst weich von similarity (0=transparent) auf similarity+blend (1=opak)
    float a = smoothstep(u_similarity, u_similarity + u_blend, d);
    // Despill: reduziere Grünanteil außerhalb voll-transparenter Bereiche
    if (u_despill > 0.0 && a > 0.0) {
        float greenSpill = max(0.0, c.g - 0.5 * (c.r + c.b));
        c.g -= greenSpill * u_despill;
    }
    gl_FragColor = vec4(c.rgb * a, a);
}`;

function hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace(/^#/, "");
    if (h.length !== 6) return [0.5, 0.63, 0.16];
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    return [r, g, b];
}

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
    const sh = gl.createShader(type);
    if (!sh) return null;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        gl.deleteShader(sh);
        return null;
    }
    return sh;
}

export default function ChromaKeyVideo({
    src, srcWebm, poster, chroma, className = "", testId, ariaLabel, onClick, onError, restartKey,
}: Props) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [failed, setFailed] = useState(false);

    // `onError` als Ref speichern, damit inline-Callbacks der Elternkomponente
    // keinen useEffect-Rebuild triggern (würde WebGL bei jedem Rerender neu
    // aufsetzen und die Video-Textur unbrauchbar machen).
    const onErrorRef = useRef(onError);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);

    // WebGL-Setup und Render-Loop, an Video-Lebenszyklus gebunden.
    useEffect(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        const gl = (canvas.getContext("webgl", { premultipliedAlpha: true, alpha: true })
            || canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
        if (!gl) {
            setFailed(true);
            onErrorRef.current?.();
            return;
        }

        const vs = compileShader(gl, gl.VERTEX_SHADER, VERT);
        const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAG);
        if (!vs || !fs) {
            setFailed(true);
            onErrorRef.current?.();
            return;
        }
        const prog = gl.createProgram();
        if (!prog) { setFailed(true); onErrorRef.current?.(); return; }
        gl.attachShader(prog, vs);
        gl.attachShader(prog, fs);
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            setFailed(true);
            onErrorRef.current?.();
            return;
        }
        gl.useProgram(prog);

        // Full-Screen-Quad.
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            -1, -1, 1, -1, -1, 1,
            -1, 1, 1, -1, 1, 1,
        ]), gl.STATIC_DRAW);
        const posLoc = gl.getAttribLocation(prog, "a_pos");
        gl.enableVertexAttribArray(posLoc);
        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

        const tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        const [kr, kg, kb] = hexToRgb(chroma.color);
        const uKey = gl.getUniformLocation(prog, "u_key");
        const uSim = gl.getUniformLocation(prog, "u_similarity");
        const uBlend = gl.getUniformLocation(prog, "u_blend");
        const uDespill = gl.getUniformLocation(prog, "u_despill");
        const uFrame = gl.getUniformLocation(prog, "u_frame");
        gl.uniform3f(uKey, kr, kg, kb);
        gl.uniform1f(uSim, chroma.similarity ?? 0.35);
        gl.uniform1f(uBlend, chroma.blend ?? 0.15);
        gl.uniform1f(uDespill, chroma.despill ?? 1.0);
        gl.uniform1i(uFrame, 0);

        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        let raf = 0;
        let stopped = false;
        const resize = () => {
            if (!video.videoWidth || !video.videoHeight) return;
            if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                gl.viewport(0, 0, canvas.width, canvas.height);
            }
        };
        const draw = () => {
            if (stopped) return;
            try {
                if (video.readyState >= 2 && !video.paused && !video.ended) {
                    resize();
                    gl.activeTexture(gl.TEXTURE0);
                    gl.bindTexture(gl.TEXTURE_2D, tex);
                    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
                    gl.clearColor(0, 0, 0, 0);
                    gl.clear(gl.COLOR_BUFFER_BIT);
                    gl.drawArrays(gl.TRIANGLES, 0, 6);
                }
            } catch {
                // texImage2D kann bei Cross-Origin / Tab hidden Fehler werfen.
            }
            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);

        return () => {
            stopped = true;
            cancelAnimationFrame(raf);
            try {
                gl.deleteTexture(tex);
                gl.deleteBuffer(buf);
                gl.deleteProgram(prog);
                gl.deleteShader(vs);
                gl.deleteShader(fs);
            } catch { /* ignore */ }
        };
        // Nur bei tatsächlichen Setup-Änderungen (Video-Source oder Chroma-
        // Parameter) neu aufsetzen. `onError` wird via Ref weitergeleitet,
        // damit inline-Callbacks der Elternkomponente hier kein Rebuild
        // triggern (sonst wird WebGL bei jedem HeaderBrandLogo-Rerender
        // neu initialisiert und die laufende Textur bricht ab).
    }, [src, chroma.color, chroma.similarity, chroma.blend, chroma.despill]);

    // Route-Change → Video zurückspulen (kommt via `restartKey`-Prop-Wechsel).
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        try {
            v.currentTime = 0;
            void v.play().catch(() => undefined);
        } catch { /* ignore */ }
    }, [restartKey]);

    if (failed) return null;

    return (
        <>
            {/* Verstecktes <video> als Frame-Source. Same-origin (`/api/media/{id}/proxy`)
                → kein `crossOrigin`-Attribut nötig, WebGL `texImage2D` funktioniert direkt.
                Cross-Origin würde CORS-Preflight + credentialed-Response-Konflikt
                triggern.

                WebM zuerst (Chrome/Firefox/Chromium ohne H.264-Lizenz nutzen VP9),
                MP4/H.264 als Fallback für Safari-Alt oder Legacy. */}
            <video
                ref={videoRef}
                poster={poster}
                autoPlay
                muted
                loop
                playsInline
                controls={false}
                preload="auto"
                onError={(e) => {
                    console.warn("[ChromaKeyVideo] <video> onError", e.currentTarget.error?.code, e.currentTarget.error?.message);
                    setFailed(true);
                    onErrorRef.current?.();
                }}
                style={{ display: "none" }}
                aria-hidden="true"
                data-testid={testId ? `${testId}-source` : undefined}
            >
                {srcWebm && <source src={srcWebm} type="video/webm" />}
                <source src={src} type="video/mp4" />
            </video>
            <canvas
                ref={canvasRef}
                className={className}
                style={{ background: "transparent" }}
                onClick={() => {
                    onClick?.();
                    const v = videoRef.current;
                    if (!v) return;
                    if (v.paused) void v.play().catch(() => undefined);
                    else v.pause();
                }}
                aria-label={ariaLabel}
                data-testid={testId}
                data-brand-logo-kind="chroma-video"
            />
        </>
    );
}
